import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { usePageTitle } from "@/lib/usePageTitle";
import { api, apiErrorMessage, rows } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { rupees } from "@/lib/format";
import { StarRating } from "@/components/StarRating";
import { useRequireAuth } from "@/lib/useRequireAuth";

export default function Services() {
  const { t, lang, tenant, auth } = useApp();
  usePageTitle(t("services.title")); // RF-21: per-page tab title
  const requireAuth = useRequireAuth();
  const [trades, setTrades] = useState([]);
  const [form, setForm] = useState({ trade: "", description: "", area: "" });
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState(false);
  // Submit is disabled without a trade, and the trades come from the network. Uncaught, a failed
  // fetch left the chips empty and the button dead with nothing said — the citizen's whole route
  // to "find me a worker" quietly unusable. The flag makes the cause visible and retryable.
  const [tradesError, setTradesError] = useState(false);
  //: Per-request rating draft, keyed by request id — one page can show several finished jobs and
  //: a single shared `stars` value would move all their star rows at once.
  const [ratings, setRatings] = useState({});

  const loadRequests = useCallback(async () => {
    if (!auth || !tenant) return;
    const { data } = await api.get("/services/requests/mine", { params: { tenant_id: tenant.id } });
    setRequests(rows(data));
  }, [auth, tenant]);

  const loadTrades = useCallback(() => {
    if (!tenant) return;
    setTradesError(false);
    api
      .get("/services/trades", { params: { tenant_id: tenant.id } })
      .then((r) => {
        setTrades(rows(r.data));
        setForm((f) => ({ ...f, trade: f.trade || r.data[0]?.trade || "" }));
      })
      .catch(() => setTradesError(true));
  }, [tenant]);

  useEffect(() => {
    loadTrades();
  }, [loadTrades]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const submit = async () => {
    // Guard BEFORE the POST (L2): a guest used to POST, get a 401 and a "Saving…" button, then the
    // global modal. Now the prompt opens first; the typed description/area stay in the form (they
    // are only cleared on success below).
    if (!requireAuth()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/services/requests", form);
      toast.success(t("services.created"));
      toast.message(t("services.workersNotified", { count: data.workers_notified }));
      setForm({ ...form, description: "", area: "" });
      await loadRequests();
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  const acceptQuote = async (quoteId) => {
    try {
      await api.post(`/services/quotes/${quoteId}/accept`);
      toast.success(t("services.accepted"));
      await loadRequests();
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    }
  };

  const rate = async (requestId) => {
    const draft = ratings[requestId];
    if (!draft?.stars) return;
    try {
      await api.post(`/services/requests/${requestId}/rate-worker`, {
        rating: draft.stars,
        body: draft.body || "",
      });
      toast.success(t("services.rateThanks"));
      setRatings((r) => ({ ...r, [requestId]: undefined }));
      await loadRequests();
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    }
  };

  const act = async (path, message) => {
    try {
      await api.post(path);
      toast.success(message);
      await loadRequests();
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    }
  };

  const statusLabels = t("services.statusLabels");

  return (
    <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8" data-testid="services-page">
      <h1 className="font-heading text-4xl font-bold tracking-tight sm:text-5xl">{t("services.title")}</h1>
      <p className="mt-3 max-w-2xl text-base text-muted-foreground">{t("services.subtitle")}</p>

      <div className="mt-10 grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
        <div className="space-y-5 rounded-2xl border border-border p-6">
          <div className="space-y-2">
            <Label>{t("services.trade")}</Label>
            {tradesError && (
              <div
                className="flex flex-wrap items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
                data-testid="service-trades-error"
              >
                {t("services.tradesFailed")}
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                  data-testid="service-trades-retry"
                  onClick={loadTrades}
                >
                  {t("common.retry")}
                </Button>
              </div>
            )}
            <div className="flex flex-wrap gap-2" data-testid="service-trade-options">
              {trades.map((tr) => (
                <button
                  key={tr.trade}
                  type="button"
                  data-testid={`service-trade-${tr.trade.toLowerCase()}`}
                  onClick={() => setForm({ ...form, trade: tr.trade })}
                  className={`rounded-full border px-4 py-1.5 text-sm ${
                    form.trade === tr.trade
                      ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground"
                      : "border-border hover:bg-muted"
                  }`}
                >
                  {lang === "hi" ? tr.trade_hi || tr.trade : tr.trade}{" "}
                  <span className="ml-1 text-xs opacity-70">
                    {t("services.tradeWorkers", { count: tr.workers })}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="service-description">{t("services.description")}</Label>
            <Textarea
              id="service-description"
              rows={4}
              data-testid="service-description-input"
              value={form.description}
              placeholder={t("services.descriptionPlaceholder")}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="service-area">{t("services.area")}</Label>
            <Input
              id="service-area"
              data-testid="service-area-input"
              value={form.area}
              placeholder={t("services.areaPlaceholder")}
              onChange={(e) => setForm({ ...form, area: e.target.value })}
            />
          </div>

          <Button
            className="rounded-full"
            data-testid="service-submit-btn"
            disabled={busy || !form.trade || form.description.trim().length < 5}
            onClick={submit}
          >
            {busy ? t("common.saving") : t("services.submit")}
          </Button>
        </div>

        <div className="space-y-4" data-testid="my-service-requests">
          <h2 className="font-heading text-2xl font-semibold tracking-tight">{t("services.myRequests")}</h2>
          {requests.length === 0 && <p className="text-sm text-muted-foreground">{t("services.myRequestsEmpty")}</p>}
          {requests.map((r) => (
            <article key={r.id} className="space-y-4 rounded-2xl border border-border p-5" data-testid={`service-request-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-heading text-base font-semibold">{lang === "hi" ? r.trade_hi : r.trade}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{r.description}</p>
                  {r.area && <p className="mt-1 text-xs text-muted-foreground">{r.area}</p>}
                </div>
                <Badge variant="outline" data-testid={`service-status-${r.id}`}>
                  {statusLabels[r.status]}
                </Badge>
              </div>

              <div className="space-y-3 border-t border-border pt-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  {t("services.quotes")}
                </p>
                {r.quotes.length === 0 && <p className="text-sm text-muted-foreground">{t("services.quotesEmpty")}</p>}
                {r.quotes.map((q) => (
                  <div
                    key={q.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border p-4"
                    data-testid={`service-quote-${q.id}`}
                  >
                    <div>
                      <p className="text-sm font-semibold">{q.worker_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {q.price !== null && q.price !== undefined
                          ? `${t("services.quotedPrice")}: ${rupees({ minor_units: q.price?.minor_units })}`
                          : t("services.willingToDo")}
                      </p>
                      {q.message && <p className="mt-1 text-sm text-muted-foreground">{q.message}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      {q.status === "accepted" ? (
                        <>
                          <Badge className="gap-1 bg-[hsl(var(--primary))] text-primary-foreground">
                            <CheckCircle2 size={12} /> {statusLabels.assigned}
                          </Badge>
                          <a href={`tel:${q.worker_phone}`} data-testid={`service-call-${q.id}`}>
                            <Button size="sm" variant="outline" className="gap-1 rounded-full">
                              <Phone size={13} /> {t("services.call")}
                            </Button>
                          </a>
                          <a
                            href={`https://wa.me/${q.worker_phone.replace(/[^\d]/g, "")}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`service-whatsapp-${q.id}`}
                          >
                            <Button size="sm" variant="outline" className="gap-1 rounded-full">
                              <MessageCircle size={13} /> {t("services.whatsapp")}
                            </Button>
                          </a>
                        </>
                      ) : q.status === "declined" ? (
                        <Badge variant="outline">{t("services.declined")}</Badge>
                      ) : (
                        <Button
                          size="sm"
                          className="rounded-full"
                          data-testid={`service-accept-${q.id}`}
                          onClick={() => acceptQuote(q.id)}
                        >
                          {t("services.acceptQuote")}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                {r.status === "open" && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                    data-testid={`service-cancel-${r.id}`}
                    onClick={() => act(`/services/requests/${r.id}/cancel`, t("services.cancelled"))}
                  >
                    {t("services.cancel")}
                  </Button>
                )}
                {r.status === "assigned" && (
                  <Button
                    size="sm"
                    className="rounded-full"
                    data-testid={`service-complete-${r.id}`}
                    onClick={() => act(`/services/requests/${r.id}/complete`, t("services.completed"))}
                  >
                    {t("services.markComplete")}
                  </Button>
                )}
              </div>

              {/* The step the web never had: a finished job you can rate. Without it a resident
                  who lives on the PWA could hire a worker and never say how it went, so a
                  worker's rating only ever reflected the people who happened to use the app. */}
              {r.status === "completed" &&
                (r.rated ? (
                  <p className="rounded-xl border border-border p-4 text-sm" data-testid={`service-rated-${r.id}`}>
                    {t("services.rateThanks")}
                  </p>
                ) : (
                  <div className="space-y-3 rounded-xl border border-border p-4" data-testid={`service-rate-${r.id}`}>
                    <p className="font-semibold">{t("services.rateTitle")}</p>
                    <StarRating
                      value={ratings[r.id]?.stars ?? 0}
                      size={22}
                      onChange={(stars) =>
                        setRatings((prev) => ({ ...prev, [r.id]: { ...prev[r.id], stars } }))
                      }
                      testId={`service-rate-stars-${r.id}`}
                    />
                    <Textarea
                      rows={2}
                      value={ratings[r.id]?.body ?? ""}
                      placeholder={t("services.rateHint")}
                      data-testid={`service-rate-body-${r.id}`}
                      onChange={(e) =>
                        setRatings((prev) => ({ ...prev, [r.id]: { ...prev[r.id], body: e.target.value } }))
                      }
                    />
                    <Button
                      size="sm"
                      className="rounded-full"
                      disabled={!ratings[r.id]?.stars}
                      data-testid={`service-rate-submit-${r.id}`}
                      onClick={() => rate(r.id)}
                    >
                      {t("services.rateSubmit")}
                    </Button>
                  </div>
                ))}
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
